// ⚡️ TRANSACTION STATUS BOX: Transaction status display with timeline and progress indicators
// Extracted from ChatPanel.js for better organization and reusability

import React, { useEffect, useMemo, useState } from 'react';
import { apiGet } from '../../../../utils/api';
import StatusTimeline from '../timeline/StatusTimeline';
import ReviewSubmissionModal from '../modals/ReviewSubmissionModal';

const TransactionStatusBox = ({
  conversation,
  isDarkMode,
  dispatch,
  state,
  isAdmin = false
}) => {
  const [order, setOrder] = useState(null);
  const [showReview, setShowReview] = useState(false);
  const [hasSubmittedAnyReview, setHasSubmittedAnyReview] = useState(false);

  useEffect(() => {
    let ignore = false;
    const load = async () => {
      if (!conversation?.order_id) return;
      try {
        const tryEndpoints = isAdmin ? ['/api/orders/'] : ['/api/orders/my', '/api/orders/sales'];
        for (const ep of tryEndpoints) {
          const res = await apiGet(ep);
          if (!res.ok) continue;
          const list = await res.json();
          const found = Array.isArray(list) ? list.find(o => String(o.order_id) === String(conversation.order_id)) : null;
          if (found) { if (!ignore) setOrder(found); break; }
        }
      } catch {}
    };
    load();
    return () => { ignore = true; };
  }, [conversation?.order_id, isAdmin]);

  // Detect if current user is the seller and if order is completed
  const isCompleted = useMemo(() => {
    const s = (order?.status || conversation.status || '').toLowerCase().replace(/[-_ ]/g, '');
    return s === 'completed' || s === 'complete';
  }, [order?.status, conversation.status]);

  const isSeller = useMemo(() => {
    try {
      const sellerEmail = (conversation?.seller?.email || '').toLowerCase();
      const auth = require('../../../../store/authStore').default;
      const current = auth?.getState ? auth.getState().user : null;
      const myEmail = (current?.email || '').toLowerCase();
      return !!sellerEmail && !!myEmail && sellerEmail === myEmail;
    } catch (_) { return false; }
  }, [conversation?.seller?.email]);

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        if (!conversation?.order_id) return;
        const res = await apiGet(`/api/reviews/order/${encodeURIComponent(conversation.order_id)}`);
        if (!ignore && res.ok) {
          const data = await res.json();
          const hasAny = Array.isArray(data.reviews) && data.reviews.length > 0;
          setHasSubmittedAnyReview(!!hasAny);
        }
      } catch (_) {}
    })();
    return () => { ignore = true; };
  }, [conversation?.order_id]);
  // Helper function to get transaction status text based on status and category
  const getTransactionStatusText = (status, category = '') => {
    if (!status) return '';
    const s = status.toLowerCase().replace(/[\s_-]/g, ''); // normalize: remove spaces, hyphens, underscores
    
    if (s === 'onhold') return 'Your payment is under checking. Once verified, your order will move to processing. No action needed — we\'ll update you soon.';
    if (s === 'processing' || s === 'inprogress') return 'We\'ve received your payment and informed the seller to transfer the account to us. Your order is now being processed.';
    
    if (s === 'nuwanisuru') {
      // Check if this is a Facebook category order
      if (category.toLowerCase().includes('facebook')) {
        return 'The seller has assigned the escrow agent as a page admin, and the escrow agent will review everything and remove other admins. We will contact you as soon as it is ready to be released.';
      }
      return 'Seller has been transferred account to our escrow agent, who is now reviewing and securing the login details. We\'ll contact you shortly once it\'s ready for release.';
    }
    
    if (s === 'nuwanis') {
      // Check if this is a Facebook category order
      if (category.toLowerCase().includes('facebook')) {
        return 'The escrow agent has transferred administrative rights to you. Please accept invite and confirm within 24 hours. If we don\'t hear from you within 24 hours, we\'ll consider the order complete and release payment to the seller.';
      }
      return (
        <>
          The account details have been successfully transferred to you.
          Please check the login info box and confirm within 24 hours. If we don't hear from you within 24 hours, we'll consider the order complete and release payment to the seller.
          <br /><br />
          <span className={`font-bold ${isDarkMode ? 'text-gray-200' : 'text-black'}`}>
            Buyer, we will change e-mail, change 2 step verification settings and delete all connected devices. So, we recommend to wait 1/2 days to be sure that nothing happen. Sometimes accounts may get locked due to a lot of security changes
          </span>
        </>
      );
    }
    
    if (s === 'completed' || s === 'complete') return 'The transaction has been completed successfully! 🎉\nWe\'d really appreciate it if you could take a moment to leave a review about your experience with us.\nYour feedback helps us grow and serve you better!';
    if (s === 'refunded') return 'Dear buyer, your refund has been processed successfully. The amount will reflect in your account within 3–5 business days. Let us know if you need any further assistance.';
    
    return 'Transaction status is being updated. Please check back soon.';
  };

  // Helper function to get status display text
  const getStatusDisplayText = (status) => {
    const s = (status || '').toLowerCase().replace(/[-_ ]/g, '');
    if (s === 'onhold') return 'Checking';
    if (s === 'refunded') return 'Refund';
    // For Paid statuses, show nothing
    return null;
  };

  const statusDisplayText = getStatusDisplayText(order?.status || conversation.status);

  return (
    <div className="w-full mt-4">
      <div className={`rounded-2xl shadow-lg p-6 transform transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${
        isDarkMode 
          ? 'bg-gradient-to-br from-[#1a1a1a] via-[#0f0f0f] to-[#1a1a1a] border border-gray-700/50' 
          : 'bg-gradient-to-br from-gray-50 via-white to-gray-50 border border-gray-200'
      }`}>
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className={`font-bold text-lg flex items-center gap-2 ${
            isDarkMode ? 'text-white' : 'text-gray-800'
          }`}>
            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-green-600 shadow-md">
              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                <path d="M8 12l2 2 4-4" stroke="currentColor" strokeWidth="2"/>
              </svg>
            </div>
            <span>Transaction Status</span>
          </div>
          
          {/* Status Display Text */}
          <div className="flex items-center gap-2">
            {statusDisplayText && (
              <span className={`font-semibold text-base ${
                statusDisplayText === 'Refund' 
                  ? 'text-blue-600' 
                  : isDarkMode ? 'text-white' : 'text-black'
              }`}>
                {statusDisplayText}
              </span>
            )}
          </div>
        </div>
        
        {/* Status Text */}
        <div className={`rounded-xl p-4 border shadow-sm ${
          isDarkMode 
            ? 'bg-[#0f0f0f] border-gray-700/50' 
            : 'bg-white border-gray-100'
        }`}>
          <div className={`text-base leading-relaxed ${
            isDarkMode ? 'text-gray-300' : 'text-gray-700'
          }`}>
            {getTransactionStatusText(order?.status || conversation.status, conversation.category_name)}
          </div>
        </div>
        
        {/* Status Timeline */}
        <StatusTimeline 
          status={order?.status || conversation.status} 
          isDarkMode={isDarkMode} 
        />

        {/* Seller Review CTA when completed */}
        {isCompleted && isSeller && (
          <div className="mt-4 flex items-center">
            <button
              type="button"
              onClick={() => setShowReview(true)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                isDarkMode ? 'bg-blue-900/50 text-blue-200 hover:bg-blue-800/50' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
              }`}
            >
              {hasSubmittedAnyReview ? 'Change Review' : 'Submit Review'}
            </button>
          </div>
        )}
      </div>

      {/* Review Modal */}
      <ReviewSubmissionModal
        isOpen={showReview}
        onClose={() => setShowReview(false)}
        order={order}
        conversation={conversation}
        user={(require('../../../../store/authStore').default?.getState?.() || {}).user}
      />
    </div>
  );
};

export default TransactionStatusBox;
